import { makeObservable } from "mobx";
import BaseObjectStore, { BaseObjectStoreParams } from "../BaseObjectStore";
import { SignalsService } from "services/SignalsService";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { SignalsRequest } from "components/Signals/SignalsModels";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";

class UniqueCardStore<TData = any> extends BaseObjectStore<
    TData,
    SignalsRequest | null
> {
    private getDataFunction: (
        signalsService: SignalsService,
        applicationFiltersStore: ApplicationFiltersStore,
        state?: SignalsRequest,
    ) => Promise<TData>;
    private getStateFunction?:
        | ((
              applicationFiltersStore: ApplicationFiltersStore,
          ) => SignalsRequest);
    private getCacheKeyPartFunction?: (
        applicationFiltersStore: ApplicationFiltersStore,
        state?: SignalsRequest,
    ) => unknown;
    private component: ({
        cardStore,
    }: {
        cardStore: UniqueCardStore<TData>;
    }) => JSX.Element;
    constructor({
        baseObject,
        rootStore,
        options,
        initialData,
        refreshOnCreate,
        getDataFunction,
        component,
        getStateFunction,
        getCacheKeyPartFunction,
    }: BaseObjectStoreParams<TData, any> & {
        getDataFunction: (
            signalsService: SignalsService,
            applicationFiltersStore: ApplicationFiltersStore,
            state?: SignalsRequest,
        ) => Promise<TData>;
        getStateFunction?: (
            applicationFiltersStore: ApplicationFiltersStore,
        ) => SignalsRequest;
        getCacheKeyPartFunction?: (
            applicationFiltersStore: ApplicationFiltersStore,
            state?: SignalsRequest,
        ) => unknown;
        component: ({
            cardStore,
        }: {
            cardStore: UniqueCardStore<TData>;
        }) => JSX.Element;
    }) {
        const shouldRefreshOnCreate = refreshOnCreate ?? false;
        super({
            baseObject,
            rootStore,
            options,
            initialData,
            refreshOnCreate: false,
        });
        makeObservable(this);
        this.getDataFunction = getDataFunction;
        this.component = component;
        this.getStateFunction = getStateFunction;
        this.getCacheKeyPartFunction = getCacheKeyPartFunction;

        if (shouldRefreshOnCreate) {
            void this.runRefresh();
        }
    }

    public preRefresh(): void {
        const mergedFilters = this.getMergedFiltersForRequest();
        if (this.getStateFunction) {
            const customState = this.getStateFunction(this.applicationFiltersStore);
            this.state = {
                ...customState,
                ...mergedFilters,
                startDate: mergedFilters.startDate ?? customState.startDate,
                endDate: mergedFilters.endDate ?? customState.endDate,
                dateReference:
                    mergedFilters.dateReferenceOption ??
                    (mergedFilters as any).dateReference ??
                    customState.dateReference,
            };
            return;
        }

        this.state = mergedFilters as SignalsRequest;
    }

    protected override getCacheFiltersPart(): unknown {
        const mergedFilters = this.getMergedFiltersForRequest();
        if (this.getStateFunction) {
            const customState = this.getStateFunction(this.applicationFiltersStore);
            return {
                ...customState,
                ...mergedFilters,
                startDate: mergedFilters.startDate ?? customState.startDate,
                endDate: mergedFilters.endDate ?? customState.endDate,
                dateReference:
                    mergedFilters.dateReferenceOption ??
                    (mergedFilters as any).dateReference ??
                    customState.dateReference,
            };
        }
        return mergedFilters;
    }

    protected override getCacheSelectionsPart(): unknown {
        return undefined;
    }

    protected override getCacheExtraParts(): unknown {
        if (!this.getCacheKeyPartFunction) {
            return undefined;
        }
        const state = this.getStateFunction
            ? this.getStateFunction(this.applicationFiltersStore)
            : this.state ?? undefined;
        return this.getCacheKeyPartFunction(
            this.applicationFiltersStore,
            state,
        );
    }

    public async getData({
        abortSignal: _abortSignal,
    }: {
        abortSignal: AbortSignal;
    }): Promise<TData> {
        this.preRefresh();

        return await this.getDataFunction(
            this.signalsService,
            this.applicationFiltersStore,
            this.state ?? undefined,
        );
    }

    public getComponent(): ({
        cardStore,
    }: {
        cardStore: UniqueCardStore<any>;
    }) => JSX.Element {
        return this.component;
    }

    public async updateUniqueConfiguration({
        title,
        subtitle,
        objectFilters,
    }: {
        title?: string;
        subtitle?: string;
        objectFilters?: string | null;
    }): Promise<boolean> {
        try {
            // isEditable means either its a standard dashboard (you can edit those selections and such) OR you have edit permission. isSaveable means you have to have permission to edit.
            // BOTH are required to actually be able to save changes to the backend, otherwise we just apply them locally for the user's current session.
            if (this.isEditable && this.isSaveable) {
                await this.signalsService.updateSignalsUniqueCard({
                    id: this.id,
                    title,
                    subtitle,
                    objectFilters,
                });
            }
            if (title !== undefined) {
                this.title = title;
            }
            if (subtitle !== undefined) {
                this.subtitle = subtitle;
            }

            return true;
        } catch (error) {
            this.rootStore
                .getStore(MessageStore)
                .logError("Failed to update card settings.");
            return false;
        }
    }
}

export default UniqueCardStore;
